<template>
  <div class="base-info-banner" :class="[`base-info-banner--${preset}`, `base-info-banner--${tone}`]">
    <div class="base-info-banner__icon">
      <slot name="icon" />
    </div>
    <div class="base-info-banner__content">
      <p v-if="title" class="base-info-banner__title">{{ title }}</p>
      <p v-if="description" class="base-info-banner__description">{{ description }}</p>
    </div>
    <div v-if="$slots.action" class="base-info-banner__action">
      <slot name="action" />
    </div>
  </div>
</template>

<script>
import { isTone, isPreset } from '@/util/design-tokens';

export default {
  name: 'BaseInfoBanner',
  props: {
    title: {
      type: String,
      default: '',
    },
    description: {
      type: String,
      default: '',
    },
    tone: {
      type: String,
      default: 'neutral',
      validator: isTone,
    },
    preset: {
      type: String,
      default: 'soft',
      validator: isPreset,
    },
  },
};
</script>

<style lang="scss" scoped>
.base-info-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  box-sizing: border-box;
  border-radius: $border-radius-root;
  background-color: var(--action-bg);
  color: var(--action-text);
  border: 1px solid var(--action-border);
  transition: background-color $duration-fast $ease-ui-interaction, border-color $duration-fast $ease-ui-interaction;

  &__icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--action-text);

    ::v-deep svg {
      width: 14px;
      height: 14px;
    }
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__title {
    font-size: $font-size-xsmall;
    font-weight: 700;
    color: var(--action-text);
    margin: 0;
  }

  &__description {
    font-size: 11px;
    font-weight: 400;
    color: var(--action-text);
    opacity: 0.8;
    line-height: 1.2;
    margin: 0;
  }

  &__action {
    flex-shrink: 0;
    display: flex;
    align-items: center;

    // Links styled like buttons; flex so they center on the title instead of riding a 16px baseline.
    ::v-deep a,
    ::v-deep button {
      display: inline-flex;
      align-items: center;
      line-height: 1.2;
      font-size: $font-size-xsmall;
      font-weight: 700;
      color: var(--action-text);
      text-decoration: underline;
      background: none;
      border: none;
      cursor: pointer;
      padding: 8px 12px;
      border-radius: $border-radius-root;
      transition: background-color $duration-fast $ease-ui-interaction;

      &:hover {
        background-color: var(--action-bg-hover);
      }
    }
  }

  @each $preset in $aloha-presets {
    @each $tone, $_ in $aloha-tones {
      &--#{$preset}.base-info-banner--#{$tone} {
        @include action-preset-hooks($preset, $tone);

        // Soft banners stay lighter than soft tags/buttons (which use 200).
        @if $preset == 'soft' {
          $palette: map-get($aloha-palettes, map-get($aloha-tones, $tone));
          --action-bg: #{map-get($palette, '100')};
          --action-bg-hover: #{map-get($palette, '100')};
          --action-bg-active: #{map-get($palette, '200')};
        }
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0ms;
  }
}
</style>
